import React, { useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import { TextField, Button, InputAdornment } from "@mui/material";
import Colors from "../../theme/colors.json";
import {
  CONFIRMATION_CONTENT,
  CONFIRMATION_TITLE,
  PREMIUM_BREAK_UP_SECTIONS,
  USER_GROUPS,
  handleValueRoundOff,
} from "../../constants/constants";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { useSelector } from "react-redux";

const useStyles = makeStyles((theme) => ({
  premiumBreakUptable: {
    width: "100%",
    borderCollapse: "collapse",
    border: `1px solid ${Colors.BORDER}`,
    marginBottom: "20px",
    "& th, & td": {
      border: `1px solid ${Colors.BORDER}`,
      padding: "8px",
      textAlign: "left",
    },
    background: Colors.WHITE_BACKGROUND,
    borderRadius: "0.6rem",
  },
  premiumBreakUptableHeading: {
    color: theme.palette.secondary.main,
    fontSize: theme.fontSizes.MEDIUM,
    fontWeight: 600,
  },
  validationContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  tableTextFieldStyles: {
    fontSize: theme.fontSizes.SMALL,
  },
  validationAction: {
    pointerEvents: "none",
  },
  commonBtnStyles: {
    background: Colors.WHITE_BACKGROUND,
    textTransform: "capitalize",
  },
  premiumBreakUpTableHeadingCell: {
    fontSize: theme.fontSizes.SMALL,
    fontWeight: 600,
  },
  premiumBreakUpTableCell: {
    fontSize: theme.fontSizes.SMALL,
  },
}));

const PremiumBreakUp = ({ premiumData, setPremiumData }) => {
  const classes = useStyles();
  const { userGroup } = useSelector((state: any) => ({
    userGroup: state.userGroup,
  }));
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const handleYes = () => {
    setConfirmDialogOpen(false);
    setPremiumData((prevData) => ({
      ...prevData,
      validation: true,
    }));
  };

  const handleNo = () => {
    setConfirmDialogOpen(false);
  };
  const premiumSections = [
    {
      label: "Net Premium / OD (other than Motor TP, Terrorism & ERF)",
      section: PREMIUM_BREAK_UP_SECTIONS.NET_PREMIUM,
    },
    {
      label: "Motor TP Premium / Terrorism / Premium for ERF in case PL Act",
      section: PREMIUM_BREAK_UP_SECTIONS.MOTORTPPREMIUM,
    },
  ];

  const handleInputChange = async (e, section) => {
    const { name, value } = e.target;
    // const newValue = !isNaN(value) ? parseFloat(value) : value;
    const newValue = value;

    await setPremiumData((prevData) => {
      const sectionData = prevData[section];
      const netPremium = sectionData.netPremium;
      const GST = sectionData.gst;

      const gstPremium: any =
        (parseFloat(newValue) * (name === "netPremium" ? GST : netPremium)) /
        100;
      const premiumWithGst: any =
        name === "netPremium"
          ? parseFloat(newValue) + gstPremium
          : gstPremium + parseFloat(netPremium);

      const totalNetPremium =
        (name === "netPremium"
          ? parseFloat(newValue)
          : parseFloat(netPremium)) +
        (section === PREMIUM_BREAK_UP_SECTIONS.NET_PREMIUM
          ? parseFloat(prevData?.motorTpPremium?.netPremium)
          : parseFloat(prevData?.netPremium?.netPremium));
      const totalGstPremium =
        parseFloat(gstPremium) +
        (section === PREMIUM_BREAK_UP_SECTIONS.NET_PREMIUM
          ? parseFloat(prevData?.motorTpPremium?.gstPremium)
          : parseFloat(prevData?.netPremium?.gstPremium));
      const totalPremiumWithGst =
        parseFloat(premiumWithGst) +
        (section === PREMIUM_BREAK_UP_SECTIONS.NET_PREMIUM
          ? parseFloat(prevData?.motorTpPremium?.premiumWithGst)
          : parseFloat(prevData?.netPremium?.premiumWithGst));

      const updatedData = {
        ...prevData,
        [section]: {
          ...sectionData,
          [name]: newValue,
          gstPremium: gstPremium,
          premiumWithGst: premiumWithGst,
        },
        totalPremium: {
          totalNetPremium: handleValueRoundOff(totalNetPremium),
          totalGstPremium: handleValueRoundOff(totalGstPremium),
          totalPremiumWithGst: handleValueRoundOff(totalPremiumWithGst),
        },
        grossPremium: {
          grossPremiumWithGst: handleValueRoundOff(totalPremiumWithGst),
        },
      };

      return updatedData;
    });
  };

  const handleFieldRendering = (section, field) => {
    switch (field) {
      case "gstPremium":
      case "premiumWithGst":
        return (
          <TextField
            type={"number"}
            name={field}
            size="small"
            disabled
            value={premiumData[section?.section][field]}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">₹</InputAdornment>
              ),
              className: classes.tableTextFieldStyles,
            }}
          />
        );
      default:
        return (
          <TextField
            type={"number"}
            name={field}
            size="small"
            // disabled={
            //   field === "gstPremium" || field === "premiumWithGst"
            //     ? true
            //     : false
            // }
            value={premiumData[section?.section][field]}
            onChange={(e) => handleInputChange(e, section?.section)}
            InputProps={{
              ...(field === "gst" && {
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }),
              ...(field !== "gst" && {
                startAdornment: (
                  <InputAdornment position="start">₹</InputAdornment>
                ),
              }),
              className: classes.tableTextFieldStyles,
            }}
          />
        );
    }
  };

  return (
    <div>
      <ConfirmationDialog
        open={confirmDialogOpen}
        setOpen={setConfirmDialogOpen}
        title={CONFIRMATION_TITLE}
        content={CONFIRMATION_CONTENT}
        handleYes={handleYes}
        handleNo={handleNo}
      />
      <table
        className={`${classes.premiumBreakUptable} ${
          premiumData?.validation ? classes.validationAction : ""
        }`}
      >
        <thead>
          <tr>
            <th colSpan={5} className={classes.premiumBreakUptableHeading}>
              Premium Break-up
            </th>
          </tr>
          <tr>
            <th></th>
            <th className={classes.premiumBreakUpTableHeadingCell}>
              Net Premium
            </th>
            <th className={classes.premiumBreakUpTableHeadingCell}>GST %</th>
            <th className={classes.premiumBreakUpTableHeadingCell}>
              GST Premium
            </th>
            <th className={classes.premiumBreakUpTableHeadingCell}>
              Premium with GST
            </th>
          </tr>
        </thead>
        <tbody>
          {premiumSections.map((section) => (
            <tr key={section?.label}>
              <td className={classes.premiumBreakUpTableCell}>
                {section?.label}
              </td>
              {Object.keys(premiumData[section?.section]).map((field) => (
                <td key={field}>
                  {handleFieldRendering(section, field)}
                  {/* <TextField
                    type={"number"}
                    name={field}
                    size="small"
                    disabled={
                      field === "gstPremium" || field === "premiumWithGst"
                        ? true
                        : false
                    }
                    value={premiumData[section?.section][field]}
                    onChange={(e) => handleInputChange(e, section?.section)}
                    InputProps={{
                      ...(field === "gst" && {
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      }),
                      ...(field !== "gst" && {
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }),
                      className: classes.tableTextFieldStyles,
                    }}
                  /> */}
                </td>
              ))}
            </tr>
          ))}

          <tr>
            <td className={classes.premiumBreakUpTableCell}>TOTAL PREMIUM</td>

            <td>
              <TextField
                type="number"
                name="netPremium"
                size="small"
                disabled
                value={premiumData?.totalPremium?.totalNetPremium}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                  className: classes.tableTextFieldStyles,
                }}
              />
            </td>
            <td>
              {/* <TextField
                type="number"
                name="gst"
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              /> */}
            </td>
            <td>
              <TextField
                type="number"
                name="gstPremium"
                size="small"
                disabled
                value={premiumData?.totalPremium?.totalGstPremium}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                  className: classes.tableTextFieldStyles,
                }}
              />
            </td>
            <td>
              <TextField
                type="number"
                name="premiumWithGst"
                size="small"
                disabled
                value={premiumData?.totalPremium?.totalPremiumWithGst}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                  className: classes.tableTextFieldStyles,
                }}
              />
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4} className={classes.premiumBreakUpTableCell}>
              GROSS PREMIUM
            </td>
            <td>
              <TextField
                type="number"
                disabled
                size="small"
                value={premiumData?.grossPremium?.grossPremiumWithGst}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                  className: classes.tableTextFieldStyles,
                }}
              />
            </td>
          </tr>
        </tfoot>
      </table>
      <div className={classes.validationContainer}>
        {userGroup === USER_GROUPS.ADMIN ? (
          <Button
            className={classes.commonBtnStyles}
            onClick={() =>
              setPremiumData((prevData) => ({
                ...prevData,
                validation: false,
              }))
            }
            variant="outlined"
            size="small"
            disabled={premiumData?.validation === true ? false : true}
          >
            Revalidation
          </Button>
        ) : (
          <div></div>
        )}
        <Button
          className={classes.commonBtnStyles}
          variant="outlined"
          size="small"
          color="primary"
          onClick={() => setConfirmDialogOpen(true)}
          disabled={premiumData?.validation === true ? true : false}
        >
          Validation
        </Button>
      </div>
    </div>
  );
};

export default PremiumBreakUp;
